import React, { useEffect, useReducer, useState } from 'react';
import { Button } from 'antd';
import * as libCon from '../community-hats-js-library/Constants.js';
import * as locCon from '../LocalConstants.js';
import {
  RefActiveParticipantsWithAllPlacements,
  RefAllEmails,
  RefAllReceivedSensorFiles,
  RefCoverageSummary,
  refreshAllDatasets,
} from '../hooks/DatasetHooks.js';
import Title from 'antd/es/typography/Title';
import { RefContextLanguage } from '../providers/LanguageProvider';
import { RefStoredValue } from '../hooks/GeneralStoreHooks.js';
import LoadingDatasets from '../components/LoadingDatasets.js';
import LocationPicker from '../components/LocationPicker.js';
import {
  CollectionDurationChart,
  HouseVisitChart,
  LoosingData,
  StatusTable,
  WearableCoverageChart,
  WearableCoverageHistoricChart,
} from '../components/GlobalCollectionStatusCharts.js';
import {
  computeCollectionDurationRecords,
  computeLossingDataRecords,
  computeStatusTable,
  computeVisitedHouses,
  computeWearableCoverage,
  computeWearableCoverageStreams,
} from '../utils/globalCollectionStatusFunctions.js';

// Local Constants
const HOUSE_VISITED = 'HOUSE_VISITED';
const WEARABLE_COVERAGE = 'WEARABLE_COVERAGE';
const STATUS_TABLE = 'STATUS_TABLE';
const LOOSING_DATA_RECORDS = 'LOOSING_DATA_RECORDS';
const COLLECTION_DURATION = 'COLLECTION_DURATION';
const HISTORICAL_WEARABLE_COVERAGE = 'HISTORICAL_WEARABLE_COVERAGE';

function DashboardGlobalCollectionStatus() {
  const i18n = RefContextLanguage();

  // User Type
  const userType = RefStoredValue(locCon.USER_TYPE);

  // Participants
  const [statusReceivedFiles, receivedFiles] = RefAllReceivedSensorFiles();
  const [statusEmails, emails] = RefAllEmails();
  const [statusCoverage, coverage] = RefCoverageSummary();
  const [statusParticipants, participants] = RefActiveParticipantsWithAllPlacements();

  const location = RefStoredValue(locCon.PROJECT_LOCATION);

  const [dashboardReady, setDashboardReady] = useState(false);

  // Actions
  const UPDATE_DATA = 'UPDATE_DATA';

  const initialState = {
    [HOUSE_VISITED]: {
      [locCon.CHART_DATA]: null,
      [locCon.DOMAIN]: null,
      [locCon.TICKS]: null,
    },
    [WEARABLE_COVERAGE]: null,
    [STATUS_TABLE]: null,
    [LOOSING_DATA_RECORDS]: null,
    [COLLECTION_DURATION]: {},
    [HISTORICAL_WEARABLE_COVERAGE]: null,
  };

  const reducer = (state, action) => {
    switch (action.type) {
      case UPDATE_DATA:
        let houseVisited = computeVisitedHouses(receivedFiles);
        let wearableCoverage = computeWearableCoverage(emails);
        let statusTable = computeStatusTable(participants);
        let expiredParticipants = computeLossingDataRecords(participants);
        let collectionDuration = computeCollectionDurationRecords(participants);
        let historicWearableCoverage = computeWearableCoverageStreams(coverage[libCon.HEART_RATE_COVERAGE]);

        setDashboardReady(true);

        return {
          ...state,
          [HOUSE_VISITED]: houseVisited,
          [WEARABLE_COVERAGE]: wearableCoverage,
          [STATUS_TABLE]: statusTable,
          [LOOSING_DATA_RECORDS]: expiredParticipants,
          [COLLECTION_DURATION]: collectionDuration,
          [HISTORICAL_WEARABLE_COVERAGE]: historicWearableCoverage,
        };

      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    let arr = [statusReceivedFiles, statusEmails, statusParticipants, statusCoverage];

    if (arr.every((s) => s === libCon.OK)) {
      dispatch({ type: UPDATE_DATA });
    } else setDashboardReady(false);
  }, [statusReceivedFiles, statusEmails, statusParticipants, statusCoverage]);

  return (
    <div className="mainContainer">
      <Title level={1}>{i18n.t('globalCollectionStatus')}</Title>
      <Button type="primary" onClick={() => refreshAllDatasets()}>
        {i18n.t('refresh')}
      </Button>

      <div style={{ marginBottom: '2vh' }}>
        <LocationPicker />
      </div>
      {dashboardReady === true ? (
        <div style={{ marginBottom: '5vh' }}>
          <StatusTable location={location} data={state[STATUS_TABLE]} />
          <div className="chart-row">
            <div className="chart-container">
              <WearableCoverageHistoricChart
                location={location}
                data={state[HISTORICAL_WEARABLE_COVERAGE][locCon.CHART_DATA]}
                domain={state[HISTORICAL_WEARABLE_COVERAGE][locCon.DOMAIN]}
                ticks={state[HISTORICAL_WEARABLE_COVERAGE][locCon.TICKS]}
              />
            </div>
            <div className="chart-container">
              <WearableCoverageChart location={location} data={state[WEARABLE_COVERAGE]} />
            </div>
          </div>
          <div className="chart-row">
            <div className="chart-container">
              <HouseVisitChart
                location={location}
                data={state[HOUSE_VISITED][locCon.CHART_DATA]}
                domain={state[HOUSE_VISITED][locCon.DOMAIN]}
                ticks={state[HOUSE_VISITED][locCon.TICKS]}
              />
            </div>
            <div className="chart-container">
              <CollectionDurationChart location={location} data={state[COLLECTION_DURATION]} />
            </div>
          </div>
          <LoosingData data={state[LOOSING_DATA_RECORDS]} location={location} userType={userType} />
        </div>
      ) : (
        <LoadingDatasets />
      )}
    </div>
  );
}

export default DashboardGlobalCollectionStatus;
